import { type DefaultContext, mutationPromise } from '@egw/clients'
import {
  type EnrollmentConfirmInformationV2Data,
  type EnrollmentConfirmInformationV2SubmitInput,
  type EnrollmentConfirmInformationV2SubmitOutput,
} from '../../../../../models'
import { createMutationHook, createQueryHook } from '../../../../../hooks'

/**
 * Get Enrollment ConfirmInformationV2 data
 * @param context - reference to the AbortController used to abort the call (optional)
 * @returns Promise<EnrollmentConfirmInformationV2Data>
 * @example const response = await getEnrollmentConfirmInformationV2Data()
 */
export const getEnrollmentConfirmInformationV2Data = async (
  context?: DefaultContext,
): Promise<EnrollmentConfirmInformationV2Data> => {
  const response = (await mutationPromise(
    {
      name: 'getEnrollmentConfirmInformationV2Data',
      query: `mutation getEnrollmentConfirmInformationV2Data($submitEnrollmentInput: SubmitEnrollmentInput!) {
        submitEnrollment(submitEnrollmentInput: $submitEnrollmentInput) {
          ... on SubmitEnrollmentSuccess {
            __typename
            path
            enrollmentData {
              userInfo {
                employmentStatus
                income
                netWorthRange
                occupation
                taxFilingStatus
                maritalStatus
                investment {
                  isBrokerDealer
                  isDirectorPolicyMakerOfCompany
                  directorPolicyMakerOfCompany
                  isPoliticallyExposedPerson
                  affiliatedPoliticalOrganizations
                  hasPoliticallyExposedImmediateFamilyMembers
                  politicallyExposedImmediateFamilyMembers
                  objective
                  experience
                }
              }
            }
          }
          ... on SubmitEnrollmentError {
            __typename
            path
          }
        }
      }`,
    },
    { submitEnrollmentInput: {} },
    context,
  )) as { submitEnrollment: EnrollmentConfirmInformationV2Data }
  return response.submitEnrollment
}

export const useGetEnrollmentConfirmInformationV2Data = createQueryHook<undefined, EnrollmentConfirmInformationV2Data>(
  getEnrollmentConfirmInformationV2Data,
)

/**
 * Submit Enrollment ConfirmInformationV2 data
 * @param context - reference to the AbortController used to abort the call (optional)
 * @returns Promise<EnrollmentConfirmInformationV2SubmitOutput>
 * @example const response = await submitEnrollmentConfirmInformationV2Data();
 */
export const submitEnrollmentConfirmInformationV2Data = async (
  enrollmentConfirmInformationV2SubmitInput: EnrollmentConfirmInformationV2SubmitInput,
  context?: DefaultContext,
): Promise<EnrollmentConfirmInformationV2SubmitOutput> => {
  const response = (await mutationPromise(
    {
      name: 'submitEnrollmentConfirmInformationV2Data',
      query: `mutation submitEnrollmentConfirmInformationV2Data($submitEnrollmentInput: SubmitEnrollmentInput!) {
        submitEnrollment(submitEnrollmentInput: $submitEnrollmentInput) {
          ... on SubmitEnrollmentSuccess {
            __typename
            missingField
            path
          }
          ... on SubmitEnrollmentError {
            __typename
            path
          }
        }
      }`,
    },
    {
      submitEnrollmentInput: enrollmentConfirmInformationV2SubmitInput,
    },
    context,
  )) as { submitEnrollment: EnrollmentConfirmInformationV2SubmitOutput }
  return response.submitEnrollment
}

export const useSubmitEnrollmentConfirmInformationV2Data = createMutationHook<
  EnrollmentConfirmInformationV2SubmitInput,
  EnrollmentConfirmInformationV2SubmitOutput
>(submitEnrollmentConfirmInformationV2Data)
